import React, { useState, useEffect, useRef } from 'react';
import {
    Trophy, Award, Star, Brain, Target, Users, ChartBar,
    MessageCircle, Shield
} from 'lucide-react';
import styles from './WhyTopSpeaks.module.css';

const WhyTopSpeaks = () => {
    const [activeSkill, setActiveSkill] = useState(null);
    const [paths, setPaths] = useState([]);
    const containerRef = useRef(null);

    const skills = [
        // Beginner Level
        {
            id: 1,
            icon: Brain,
            title: "Overcome Speaking Anxiety",
            description: "Build natural confidence through daily practice sessions.",
            level: 'Beginner',
            position: { row: 1, col: 1 },
            connects: [2]
        },
        // Novice Level
        {
            id: 2,
            icon: Target,
            title: "Perfect Your Timing",
            description: "Master pace and timing in your presentations.",
            level: 'Novice',
            position: { row: 1, col: 2 },
            connects: [3]
        },
        // Intermediate Level
        {
            id: 3,
            icon: Users,
            title: "Enhanced Body Language",
            description: "Develop powerful gestures and eliminate distractions.",
            level: 'Intermediate',
            position: { row: 1, col: 3 },
            connects: [4]
        },
        // Advanced Level
        {
            id: 4,
            icon: MessageCircle,
            title: "Vocal Mastery",
            description: "Enhance your vocal tone and refine your accent.",
            level: 'Advanced',
            position: { row: 2, col: 1 },
            connects: [5]
        },
        // Proficient Level
        {
            id: 5,
            icon: ChartBar,
            title: "Track Your Progress",
            description: "Monitor improvement through data-driven feedback.",
            level: 'Proficient',
            position: { row: 2, col: 2 },
            connects: [6]
        },
        // Expert Level
        {
            id: 6,
            icon: Shield,
            title: "Handle Q&A Like a Pro",
            description: "Address unexpected questions with confidence.",
            level: 'Expert',
            position: { row: 2, col: 3 },
            connects: [7]
        },
        // Elite Level
        {
            id: 7,
            icon: Award,
            title: "Advanced Speaking Patterns",
            description: "Master complex speaking techniques and patterns.",
            level: 'Elite',
            position: { row: 3, col: 1 },
            connects: [8]
        },
        // Master Level
        {
            id: 8,
            icon: Trophy,
            title: "Teaching & Mentoring",
            description: "Guide others in their speaking journey.",
            level: 'Master',
            position: { row: 3, col: 2 },
            connects: [9]
        },
        // Grandmaster Level
        {
            id: 9,
            icon: Star,
            title: "Innovation in Speaking",
            description: "Create new speaking techniques and methodologies.",
            level: 'Grandmaster',
            position: { row: 3, col: 3 },
            connects: [10]
        },
        // Legendary Level
        {
            id: 10,
            icon: Star,
            title: "Speaking Legacy",
            description: "Leave a lasting impact on the speaking community.",
            level: 'Legendary',
            position: { row: 4, col: 2 },
            connects: []
        }
    ];

    useEffect(() => {
        const calculatePaths = () => {
            if (!containerRef.current) return;

            const newPaths = [];
            const container = containerRef.current;
            const containerRect = container.getBoundingClientRect();

            skills.forEach(skill => {
                const startEl = container.querySelector(`[data-skill-id="${skill.id}"]`);
                if (!startEl) return;

                skill.connects.forEach(targetId => {
                    const endEl = container.querySelector(`[data-skill-id="${targetId}"]`);
                    if (!endEl) return;

                    const startRect = startEl.getBoundingClientRect();
                    const endRect = endEl.getBoundingClientRect();

                    const start = {
                        x: startRect.left + startRect.width / 2 - containerRect.left,
                        y: startRect.top + startRect.height / 2 - containerRect.top
                    };

                    const end = {
                        x: endRect.left + endRect.width / 2 - containerRect.left,
                        y: endRect.top + endRect.height / 2 - containerRect.top
                    };

                    newPaths.push({
                        id: `${skill.id}-${targetId}`,
                        d: `M ${start.x},${start.y} L ${end.x},${end.y}`,
                        isActive: activeSkill === skill.id || activeSkill === targetId
                    });
                });
            });

            setPaths(newPaths);
        };

        calculatePaths();
        window.addEventListener('resize', calculatePaths);

        return () => window.removeEventListener('resize', calculatePaths);
        // eslint-disable-next-line
    }, [activeSkill]);

    return (
        <div className={styles.container}>
            <h2 className={styles.title}>Develop Skills that Last a Lifetime</h2>
            <p className={styles.subtitle}>Progress through 10 levels of speaking mastery</p>
            <div className={styles.skillTree} ref={containerRef}>
                <svg className={styles.connections}>
                    {paths.map(path => (
                        <path
                            key={path.id}
                            d={path.d}
                            className={`${styles.path} ${path.isActive ? styles.activePath : ''}`}
                        />
                    ))}
                </svg>

                {skills.map(skill => {
                    const IconComponent = skill.icon;
                    return (
                        <div
                            key={skill.id}
                            data-skill-id={skill.id}
                            className={`${styles.skillCard} ${styles[`level-${skill.level.toLowerCase()}`]} ${activeSkill === skill.id ? styles.active : ''}`}
                            style={{
                                gridRow: skill.position.row,
                                gridColumn: skill.position.col
                            }}
                            onMouseEnter={() => setActiveSkill(skill.id)}
                            onMouseLeave={() => setActiveSkill(null)}
                        >
                            <div className={styles.iconWrapper}>
                                <IconComponent size={20} />
                            </div>
                            <h3>{skill.title}</h3>
                            <p>{skill.description}</p>
                            <span className={styles.levelBadge}>{skill.level}</span>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default WhyTopSpeaks;